import { useDrag } from "@use-gesture/react"
import { tv } from "@utils/tw"
import { motion } from "motion/react"
import { useMemo, useRef, useState } from "react"

type Props = {
  letters: Set<string>
  onSelect: (letter: string, dragging: boolean) => void
}

const LETTERS = "#ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")

export function AlphaBar(props: Props) {
  const container = useRef<HTMLDivElement>(null)
  const bounds = useRef<DOMRect | null>(null)
  const [activeLetter, setActiveLetter] = useState<string | null>(null)

  const bind = useDrag(
    (e) => {
      const isMobile = window.innerWidth < 900
      if (e.first) {
        bounds.current = container.current?.getBoundingClientRect() ?? null
      }
      const cur = isMobile ? e.xy[1] : e.xy[0]
      const pos = (isMobile ? bounds.current?.top : bounds.current?.left) ?? 1
      const total = (isMobile ? bounds.current?.height : bounds.current?.width) ?? 1
      const progress = (cur - pos) / total
      const index = Math.min(LETTERS.length - 1, Math.max(0, Math.floor(progress * LETTERS.length)))
      const letter = LETTERS[index]
      if (props.letters.has(letter)) {
        props.onSelect(letter, true)
      }
      setActiveLetter(letter)
      if (e.last) {
        setActiveLetter(null)
      }
    },
    {
      pointer: { touch: true, capture: true, mouse: false },
      eventOptions: { capture: true, passive: true },
      filterTaps: false,
    },
  )

  return (
    <div className="fixed inset-0 top-headerHeight z-10 pointer-events-none select-none" {...bind()}>
      <div className="w-grid mx-auto h-full relative">
        <div className="absolute flex flex-col gap-[0.4em] items-center justify-center top-0 md:top-auto bottom-0 left-full -right-grid-margin md:right-auto md:left-auto md:w-full md:bottom-5">
          <div
            className="flex flex-col md:flex-row items-center justify-center type-label-caps pointer-events-auto w-full touch-none md:w-auto md:bg-bg md:rounded-full md:shadow-popover"
            ref={container}
          >
            {LETTERS.map((letter) => (
              <button
                key={letter}
                className="text-fg transition-opacity cursor-pointer disabled:pointer-events-none disabled:opacity-20 w-full flex justify-center md:items-center md:w-6 md:h-6 select-none"
                disabled={!props.letters.has(letter)}
                onFocus={(e) => {
                  e.preventDefault()
                }}
                onMouseDown={(e) => {
                  e.currentTarget.blur()
                  e.preventDefault()
                  if (props.letters.has(letter)) {
                    props.onSelect(letter, false)
                  }
                }}
              >
                <span
                  className="aspect-square flex items-center justify-center h-[1.3em] md:[button:not(:disabled):hover_&]:bg-bg-mid data-[active='true']:bg-bg-mid data-[active='false']:!bg-transparent rounded-[0.3em] md:w-5 md:h-5 md:rounded-full"
                  data-active={activeLetter === letter ? "true" : activeLetter ? "false" : undefined}
                >
                  {letter}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
